/**
 * 我的（T6）—— 个人中心（视觉对齐 docs/操作人员移动端 设计稿）。
 * 用户卡（渐变方形头像 + 姓名 + 岗位/驻点）+ 工作统计三卡 + 菜单列表 + 退出登录。
 * 数据：GET /fulfillment/todo/count（待办数）、todo?tab=TO_DO（逾期数）、todo?tab=DONE（累计已办）。
 */
import { Box, Typography, ButtonBase, CircularProgress, Snackbar, Alert } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRole } from '@/store/RoleContext';
import { getTodoCount, getTodo } from '@/api/fulfillment';
import { MobileAppBar, StatGrid, cardSx } from '@/components/mobile/ui';
import { T } from '@/theme/tokens';

interface MenuItemDef {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  danger?: boolean;
  value?: string;
}

export default function Me() {
  const navigate = useNavigate();
  const { loginInfo, logout } = useRole();
  const [todoCount, setTodoCount] = useState<number | null>(null);
  const [doneCount, setDoneCount] = useState<number | null>(null);
  const [overdueCount, setOverdueCount] = useState<number | null>(null);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    // 后端 count 返回 Result<Long>，data 可能为字符串 "0"；统一 Number 兜底，失败显示 0 而非一直转圈
    getTodoCount()
      .then((n) => setTodoCount(Number(n) || 0))
      .catch(() => setTodoCount(0));
    getTodo('DONE')
      .then((list) => setDoneCount(Array.isArray(list) ? list.length : 0))
      .catch(() => setDoneCount(0));
    getTodo('TO_DO')
      .then((list) => setOverdueCount(Array.isArray(list) ? list.filter((t) => t.overdueFlag).length : 0))
      .catch(() => setOverdueCount(0));
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/mobile/login', { replace: true });
  };

  const soon = (label: string) => () => setSnack({ msg: `${label}即将上线，敬请期待`, sev: 'info' });
  const num = (v: number | null) => (v === null ? <CircularProgress size={18} /> : v);

  const menu: MenuItemDef[] = [
    { key: 'archive', label: '我的工作档案', icon: <DescriptionOutlinedIcon />, onClick: () => navigate('/mobile/tasks?tab=DONE') },
    { key: 'help', label: '帮助与反馈', icon: <HelpOutlineIcon />, onClick: soon('帮助与反馈') },
    { key: 'settings', label: '设置', icon: <SettingsOutlinedIcon />, onClick: soon('设置') },
    { key: 'about', label: '关于蓝星', icon: <InfoOutlinedIcon />, onClick: soon('关于蓝星') },
    { key: 'logout', label: '退出登录', icon: <LogoutIcon />, onClick: handleLogout, danger: true },
  ];

  const name = loginInfo?.displayName || loginInfo?.name || '未登录';
  const roleLine = [loginInfo?.title ?? '现场操作员', loginInfo?.location].filter(Boolean).join(' · ');

  return (
    <Box sx={{ pb: 2 }}>
      <MobileAppBar title="我的" />

      <Box sx={{ px: 2, pt: 2 }}>
        {/* 用户卡 */}
        <Box
          sx={{
            ...cardSx,
            display: 'flex',
            alignItems: 'center',
            gap: '13px',
            p: 2,
            background: 'linear-gradient(135deg,#fff 0%,#fbfdff 100%)',
          }}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '14px',
              background: T.brandGrad,
              color: '#fff',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(31,111,229,.30)',
            }}
          >
            {loginInfo?.initial ?? '?'}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: '16px', fontWeight: 700, color: T.ink }}>
              {name}
            </Typography>
            <Typography noWrap sx={{ fontSize: '12.5px', color: T.ink2, mt: '3px' }}>
              {roleLine}
            </Typography>
          </Box>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '11px',
              fontWeight: 600,
              color: T.greenInk,
              bgcolor: T.greenSoft,
              px: '9px',
              py: '3px',
              borderRadius: T.rPill,
              flexShrink: 0,
            }}
          >
            <Box
              component="i"
              sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: T.green, boxShadow: '0 0 0 3px rgba(24,160,107,.18)' }}
            />
            在线
          </Box>
        </Box>

        {/* 工作统计 */}
        <StatGrid
          sx={{ mt: '14px' }}
          items={[
            { label: '待办任务', value: num(todoCount), tone: 'amber', onClick: () => navigate('/mobile/tasks') },
            { label: '已逾期', value: num(overdueCount), tone: 'danger', onClick: () => navigate('/mobile/tasks') },
            { label: '累计完成', value: num(doneCount), tone: 'green', onClick: () => navigate('/mobile/tasks?tab=DONE') },
          ]}
        />

        {/* 菜单 */}
        <Box sx={{ ...cardSx, overflow: 'hidden', mt: '14px' }}>
          {menu.map((m, i) => (
            <ButtonBase
              key={m.key}
              onClick={m.onClick}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '13px',
                px: 2,
                py: '15px',
                fontFamily: 'inherit',
                textAlign: 'left',
                borderTop: i === 0 ? 'none' : `1px solid ${T.line}`,
                '&:active': { bgcolor: T.line2 },
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '10px',
                  bgcolor: m.danger ? T.dangerSoft : T.brandSoft,
                  color: m.danger ? T.danger : T.brand,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  '& .MuiSvgIcon-root': { fontSize: 19 },
                }}
              >
                {m.icon}
              </Box>
              <Typography sx={{ flex: 1, fontSize: '14.5px', fontWeight: 600, color: m.danger ? T.danger : T.ink }}>
                {m.label}
              </Typography>
              {m.value && <Typography sx={{ fontSize: '13px', color: T.ink3, fontWeight: 500 }}>{m.value}</Typography>}
              {!m.danger && <ChevronRightIcon sx={{ fontSize: 18, color: T.ink4 }} />}
            </ButtonBase>
          ))}
        </Box>
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnack(null)}
        sx={{ bottom: { xs: 80 } }}
      >
        <Alert severity={snack?.sev} sx={{ fontSize: '13px' }}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
